import { AttributeHelper } from "./Helpers";
import { PlanogramObject } from "./PlanogramObject";
import * as THREE from "three";
import { PlanogramRendererResources } from "./Resources";
import { PlanogramContainer } from "./PlanogramContainer";


/**
 * @class PlanogramText
 * @extends PlanogramObject
 * @description Represents a label or a an annotation in the planogram. 
 */
export class PlanogramText extends PlanogramObject {
    
    static helperAttributes ={
        ShowMargin: "show-margin",  
    }

    /**
     * Apply renderer-level behaviour for showing positions through blocking
     * @param {boolean} showOnTop
     */
    applyShowPositionsThroughBlocking(showOnTop){
        if(this.#mesh){
            this.#mesh.renderOrder = showOnTop ? 30 : 3;
        }
        if(this.#frontMesh){
            this.#frontMesh.renderOrder = showOnTop ? 30 : 3;
        }
    }

    static attributes = {
        //numbers
        BorderThickness: "border-thickness",
        BorderColour: "border-colour",
        BackgroundColour: "background-colour",
        FontSize: "font-size",
        MinimumFontSize: "minimum-font-size",
        FontColour: "font-colour",
        Width: "width",
        Height: "height",
        Depth: "depth",
        Margin: "margin",

        //strings
        FontFamily: "font-family",

        //enums
        TextWrapping: "text-wrapping",
        TextDirection: "text-direction",
        TextAlignment: "text-alignment",
        VerticalAlignment: "vertical-alignment",
        HorizontalAlignment: "horizontal-alignment",

        //booleans
        Bold: "bold",
        Italic: "italic",
        Underline: "underline",
        ShrinkToFit: "shrink-to-fit",
        RotateToFit: "rotate-to-fit",
        OverImages: "over-images",
        FollowCamera: "follow-camera"

    }

    static observedAttributes = [
        ...Object.values(PlanogramText.attributes), 
        ...Object.values(PlanogramObject.Attributes), 
        ...Object.values(PlanogramText.helperAttributes)
    ];
    
    //#region Attribute Properties
    get showMargin(){ return this.hasAttribute(PlanogramText.helperAttributes.ShowMargin); }
    get borderThickness(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.BorderThickness, 0.25); }
    get borderColour(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.BorderColour, 0); }
    get backgroundColour(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.BackgroundColour, 0xffffff); }
    get fontSize(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.FontSize, 20); }
    get minimumFontSize(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.MinimumFontSize, 2); }
    get fontColour(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.FontColour, 0); }
    get width(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.Width, 1); }
    get height(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.Height, 1); }
    get depth(){ return this.#helper.numberFromAttribute(PlanogramText.attributes.Depth, 1); }
    get fontFamily(){ return this.getAttribute(PlanogramText.attributes.FontFamily) || "Arial"; }
    get textWrapping(){ return this.#helper.enumFromAttribute(PlanogramText.attributes.TextWrapping, PlanogramText.textWrapping, PlanogramText.textWrapping.Word); }
    get textDirection(){ return this.#helper.enumFromAttribute(PlanogramText.attributes.TextDirection, PlanogramText.textDirection, PlanogramText.textDirection.Horizontal); }
    get textAlignment(){ return this.#helper.enumFromAttribute(PlanogramText.attributes.TextAlignment, PlanogramText.textAlignment, PlanogramText.textAlignment.Left); }
    get verticalAlignment(){ return this.#helper.enumFromAttribute(PlanogramText.attributes.VerticalAlignment, PlanogramText.verticalPlacement, PlanogramText.verticalPlacement.Center); }
    get horizontalAlignment(){ return this.#helper.enumFromAttribute(PlanogramText.attributes.HorizontalAlignment, PlanogramText.horizontalPlacement, PlanogramText.horizontalPlacement.Center); }
    get bold(){ return this.hasAttribute(PlanogramText.attributes.Bold); }
    get italic(){ return this.hasAttribute(PlanogramText.attributes.Italic); }
    get underline(){ return this.hasAttribute(PlanogramText.attributes.Underline); }
    get shrinkToFit(){ return this.hasAttribute(PlanogramText.attributes.ShrinkToFit); }
    get rotateToFit(){ return this.hasAttribute(PlanogramText.attributes.RotateToFit); }
    get overImages(){ return this.hasAttribute(PlanogramText.attributes.OverImages); }
    
    //#endregion

    #firstRender = true;
    #helper = new AttributeHelper(this,(96/72));
    /**@type {THREE.Mesh} */
    #mesh = null;
    /**@type {THREE.Mesh} */
    #frontMesh = null;

    /**@type {{ imagedata: ImageData, innerWidth: number, innerHeight: number }} */
    #xData = null;
    /**@type {{ imagedata: ImageData, innerWidth: number, innerHeight: number }} */
    #yData = null;
    /**@type {{ imagedata: ImageData, innerWidth: number, innerHeight: number }} */
    #zData = null;

    #observer = new MutationObserver(this.#mutationCallback.bind(this));
    #uid = THREE.MathUtils.generateUUID();

    get data(){ return { x:this.#xData, y:this.#yData, z:this.#zData }; }
    get uid(){ return this.#uid; }
    get isAttached(){ return this.isConnected && this.parentElement instanceof PlanogramObject; }
    /**@type {THREE.Vector3} */
    get size(){ return this.isAttached ? this.parentElement.groupBoundingBox.getSize(new THREE.Vector3()) : 
        new THREE.Vector3(this.width, this.height, this.depth); }

    get #context(){ return this.#canvas?.getContext('2d') ?? null; }
    get #textSplit(){ return this.textWrapping === PlanogramText.textWrapping.Letter ? 
        this.innerText.split(""): this.textWrapping === PlanogramText.textWrapping.Word ?
        this.innerText.split(" ") : [this.innerText];}
    get #longestText(){return this.#textSplit.map(x=>this.#context.measureText(x).width).reduce((a,c)=>c >a ?c:a,0);} 
    get #wordHeight(){ return this.#actualFontSize * 1.3 ; }
    
    /**@type {PlanogramContainer} */
    #container = null;
    /**@type {HTMLCanvasElement} */
    #canvas = null;
    #actualFontSize = 0;
    #actualTextDirection = PlanogramText.textDirection.Horizontal;
    constructor(){ 
        super();
    }

    /**
     * 
     * @param {number} width 
     * @param {number} height 
     * @param {boolean} boxSized 
     * @returns {{ width: number, height: number }}
     */
    #getDimensions( width, height, boxSized = false ){
        return {
            width: (this.#actualTextDirection === PlanogramText.textDirection.Horizontal ? width : height),
                // - ( boxSized ? (this.margin * 2) + (this.borderThickness * 2 ) : 0 ),
            height: (this.#actualTextDirection === PlanogramText.textDirection.Horizontal ? height : width) 
                // - ( boxSized ? (this.margin * 2) + (this.borderThickness * 2 ) : 0 )
        }
    }

    /**
     * 
     * @param {number} maxWidth 
     * @param {number} maxHeight 
     */
    #applyFontTransforms( maxWidth, maxHeight ){

        const horizontal = PlanogramText.textDirection.Horizontal;
        const vertical = PlanogramText.textDirection.Vertical;
        const text = this.textWrapping === PlanogramText.textWrapping.Letter ? 
            this.innerText.split(""): this.textWrapping === PlanogramText.textWrapping.Word ?
            this.innerText.split(" ") : [this.innerText]

        const scaleFactor = 4;
        const canvas = document.createElement('canvas');
        canvas.width = PlanogramTextController.maxRestriction.width;
        canvas.height = PlanogramTextController.maxRestriction.width;
        const context = canvas.getContext('2d');
        context.scale(scaleFactor, scaleFactor);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = `#${this.backgroundColour.toString(16)}`;
        context.textBaseline = "top";
        const margin = 0.1 + this.borderThickness;

        const getDimensions = textDirection => {
            return {
                width: textDirection === horizontal ? maxWidth : maxHeight,
                height: textDirection === horizontal ? maxHeight : maxWidth
            }
        }

        const getInnerDimensions = ( width, height ) => {
            const scale = 2;
            return {
                innerMaxWidth: Math.max((width * scale) - (margin * 2), 0.1),
                innerMaxHeight: Math.max((height * scale) - (margin * 2), 0.1),
            }
        }

        const roundToDecimalPlaces = (num, decimalPlaces) => {
            const factor = Math.pow(10, decimalPlaces);
            return Math.round(num * factor) / factor;
        }

        const getLongestLine = text => roundToDecimalPlaces(text.map(x=>context.measureText(x).width).reduce((a,c)=>c >a ?c:a,0),1);

        /**
         * 
         * @param {number} fontSize 
         */
        const setFontToContext = fontSize =>{            
            context.strokeStyle = `#${this.fontColour.toString(16)}`;
            context.lineWidth = 1;
            context.font = `${ fontSize * (96/72) }px ${this.fontFamily}`;
            context.textRendering = "optimizeLegibility"
            if(this.bold) context.font = `bold ${context.font}`;
            if(this.italic) context.font = `italic ${context.font}`;
        }

        /**
         * 
         * @param {number} width
         * @returns {string[]}
         */
        const getLines = width => {
            return text.reduce(( a, c, i ) => {
                const space = i > 0 && this.textWrapping === PlanogramText.textWrapping.Word? " " : "";
                var combinedText = (a[ a.length - 1 ] || "") + space + c;
                const textWidth = getLongestLine([combinedText]);
                if (i === 0 || textWidth >= width) a.push(c);
                else  a[a.length - 1] += space + c;
                return a;
            },[]);
        }

        

        /**
         * 
         * @param {number} fontSize
         * @param {number} width 
         * @param {number} height 
         * @returns {number}
         */
        const shrinkText = ( fontSize, width, height ) => {

            const getValues = () =>{
                const lines = getLines(width);
                return { longestWidth: roundToDecimalPlaces(lines.map(x=>context.measureText(x).width).reduce((a,c)=>c >a ?c:a,0),1), 
                    longestHeight : lines.length * (fontSize * 1.3) };
            }

            setFontToContext( fontSize );

            var { longestWidth, longestHeight } = getValues();
            while( longestWidth > width || longestHeight > height ){
                setFontToContext( fontSize -= 0.01 );
                var { longestWidth, longestHeight } = getValues();
            }

            return fontSize;
        }

        const directions = this.rotateToFit && this.isAttached ?  [ horizontal, vertical ] : [ this.textDirection ];

        const { fontSize, textDirection, lines, longestWidth, longestHeight } = directions.map( textDirection => {
            const { width, height } = getDimensions( textDirection );
            const { innerMaxWidth, innerMaxHeight } = getInnerDimensions(width,height);
            const fontSize = this.shrinkToFit || !this.isAttached? shrinkText(this.fontSize * 0.5, innerMaxWidth, innerMaxHeight) : this.fontSize * 0.5;
            const lines = getLines(innerMaxWidth);
            const longestWidth = getLongestLine(lines);
            const longestHeight = lines.length * (fontSize * 1.3); 
            return { fontSize, textDirection, lines, longestWidth, longestHeight };
        }).reduce((a,c)=>(a.longestWidth > c.longestWidth) ? a : c);
        const width = Math.min( longestWidth - margin, maxWidth );
        const height = Math.min( longestHeight - margin, maxHeight );
        setFontToContext(fontSize);

        context.lineWidth = 1;
        lines.forEach((line, i) => {
            const lineWidth = getLongestLine([line]);
            const offset = this.textAlignment === PlanogramText.textAlignment.Center ? (longestWidth - lineWidth/2) - longestWidth/2 : 
                this.textAlignment === PlanogramText.textAlignment.Right ? longestWidth - longestWidth : 0;
            context.fillStyle = "#000000";
            context.fillText(line, offset, i * (fontSize * 1.3));
        });

        var imagedata = context.getImageData(0, 0, canvas.width, canvas.height);
        return { imagedata, innerWidth: longestWidth, innerHeight: longestHeight, textDirection };
    }

    /**
     * 
     * @param {string} text 
     * @param {number} givenSpaceWidth
     * @returns {string}
     */
    #applyEllipses( text, givenSpaceWidth ){
        var resultText = "";
        const trimText = text.split("");
        for(let j = 0; j < trimText.length; j++){
            var test = resultText + trimText[j] + "...";
            var testWidth = this.roundToDecimalPlaces(this.#context.measureText(test).width,1);
            console.log(resultText, test, testWidth, givenSpaceWidth);
            if( testWidth > givenSpaceWidth)
                break;
            resultText += trimText[j];
        }
        return resultText + (resultText.length < text.length ? "..." : "");
    }

    roundToDecimalPlaces(num, decimalPlaces) {
        const factor = Math.pow(10, decimalPlaces);
        return Math.round(num * factor) / factor;
    }

    /**
     * 
     * @param {number} maxWidth 
     * @param {number} maxHeight 
     * @returns {string[]}
     */
    #generateLines( maxWidth, maxHeight, applyEllipses = false ){
        var { width, height } = this.#getDimensions(maxWidth, maxHeight, true);
        var reachedEnd = false;
        var lines = this.#textSplit.reduce(( a, c, i ) => {
            if(reachedEnd) return a;
            const space = i > 0 && this.textWrapping === PlanogramText.textWrapping.Word? " " : "";
            var combinedText = (a[ a.length - 1 ] || "") + space + c;
            const textWidth = this.#context.measureText(combinedText).width;
            const isOverFlowingX =  i === 0 || textWidth >= width;
            const willOverFlowY = isOverFlowingX && (a.length + 1) * this.#wordHeight >= height;
            const isOverflowingY = a.length * this.#wordHeight >= height;
            if(willOverFlowY && !isOverflowingY && applyEllipses){
                const text = this.#applyEllipses(combinedText, width);
                if(i === 0){
                    a.push(text);
                }else{
                    a[a.length - 1] = text;
                }
                reachedEnd = true;
            }else{
                if (i === 0 || textWidth >= width) {
                    a.push(c);
                } else {
                    a[a.length - 1] += space + c;
                }
            }
            return a;
        },[]);

        if( applyEllipses && this.textWrapping !== PlanogramText.textWrapping.Letter){
            const longestLine =  lines.map(x=>this.roundToDecimalPlaces(this.#context.measureText(x).width,1)).reduce((a,c)=>c >a ?c:a,0);
            if(longestLine > width)
                lines = lines.map(x=>this.#applyEllipses(x, width));
        }
        return lines;
    }

    #generateLabels(){

        this.#xData = this.#applyFontTransforms( this.size.z, this.size.y );
        this.#yData = this.#applyFontTransforms( this.size.x, this.size.z );
        this.#zData = this.#applyFontTransforms( this.size.x, this.size.y  );
        this.#container.textController.updateImage(this);
        
    }

    updateAttributes(){
        const hAlignment = this.horizontalAlignment === PlanogramText.horizontalPlacement.Left ? -1 :
            this.horizontalAlignment === PlanogramText.horizontalPlacement.Center ? 0 : 1;

        const vAlignment = this.verticalAlignment === PlanogramText.verticalPlacement.Top ? -1 :
            this.verticalAlignment === PlanogramText.verticalPlacement.Center ? 0 : 1;

        var [ x,y,z ] = this.#container.textController.getImageDepth(this);

        const faceOptions = Array(24).fill([...this.size.toArray(), this.isAttached? 1: 0]).flat();
        const labelOptions = [
            Array(8).fill(this.#xData).flatMap(x=>[x.innerWidth, x.innerHeight, hAlignment, vAlignment]), 
            Array(8).fill(this.#yData).flatMap(x=>[x.innerWidth, x.innerHeight, hAlignment, vAlignment]), 
            Array(8).fill(this.#zData).flatMap(x=>[x.innerWidth, x.innerHeight, hAlignment, vAlignment])
        ].flat();

        const boxOptions = [
            Array(8).fill(this.#xData).flatMap(x=>[ this.borderThickness, x, this.#xData.textDirection === PlanogramText.textDirection.Horizontal ? 0 : 1]), 
            Array(8).fill(this.#yData).flatMap(x=>[ this.borderThickness, y, this.#yData.textDirection === PlanogramText.textDirection.Horizontal ? 0 : 1]), 
            Array(8).fill(this.#zData).flatMap(x=>[ this.borderThickness, z, this.#zData.textDirection === PlanogramText.textDirection.Horizontal ? 0 : 1])
        ].flat();

        const colourOptions = Array(24).fill(this.#xData).flatMap(x=>[ this.borderColour, this.backgroundColour, this.fontColour ]);
        
        this.#mesh.geometry.setAttribute("labelOptions", new THREE.Float32BufferAttribute( labelOptions, 4 ));
        this.#mesh.geometry.setAttribute("boxOptions", new THREE.Float32BufferAttribute( boxOptions, 3 ));
        this.#mesh.geometry.setAttribute("faceOptions", new THREE.Float32BufferAttribute( faceOptions, 4 ));
        this.#mesh.geometry.setAttribute("colourOptions", new THREE.Float32BufferAttribute( colourOptions, 3 ));

        if(this.#frontMesh){
            this.#frontMesh.geometry.setAttribute("labelOptions", new THREE.Float32BufferAttribute( labelOptions.slice( 64, 96) , 4 ));
            this.#frontMesh.geometry.setAttribute("boxOptions", new THREE.Float32BufferAttribute( boxOptions.slice((boxOptions.length/3) * 2, boxOptions.length), 3 ));
            this.#frontMesh.geometry.setAttribute("faceOptions", new THREE.Float32BufferAttribute( faceOptions.slice( 64, 96), 4 ));
            this.#frontMesh.geometry.setAttribute("colourOptions", new THREE.Float32BufferAttribute( colourOptions.slice((colourOptions.length/3) * 2, colourOptions.length), 3 ));
        }
    }

    /**
     * 
     * @param {MutationRecord[]} mutations 
     */
    #mutationCallback( mutations ){
        const charMutations = mutations.filter( m => m.type === "characterData" );
        const childListMutations = mutations.filter( m => m.type === "childList" && m.addedNodes && m.addedNodes.length > 0 );
        if(charMutations.length > 0 || childListMutations.length > 0){
            const hasTextNode = Array.from(this.childNodes).some(n => n.nodeType === Node.TEXT_NODE);
            if(!hasTextNode && this.innerText === "")
                this.appendChild(document.createTextNode(""));
            this.#generateLabels();
        }
    }

    #updateMesh(){
        if(this.#container){
            const textShader = this.#container.textShader;
            if(!this.#mesh){
                this.#mesh = new THREE.Mesh(new THREE.BoxGeometry( 1, 1, 1 ), textShader);
                this.addMesh(this.#mesh, "TextBox");
            }

            if(this.isAttached && !this.#frontMesh){
                this.#frontMesh = new THREE.Mesh( new THREE.PlaneGeometry( 1, 1 ), textShader );
                this.#frontMesh.layers.set(PlanogramRendererResources.PERSPECTIVE_LAYER);
                this.#frontMesh.renderOrder = 1;
                this.addMesh(this.#frontMesh, "TextLabel");
            }else if(!this.isAttached && this.#frontMesh)
                this.#disposefrontMesh();
            if(this.isAttached){
                if(this.parentElement instanceof PlanogramObject){
                    const size = this.parentElement.groupBoundingBox.getSize(new THREE.Vector3());
                    size.add(new THREE.Vector3(0.1, 0.1, 0.1));
                    this.#mesh.scale.set(size.x, size.y, size.z);
                    this.#mesh.position.set( this.parentElement.x, this.parentElement.y, this.parentElement.z );
                    this.#frontMesh.scale.set(size.x, size.y);
                    this.#frontMesh.position.set(this.parentElement.x,this.parentElement.y,  this.parentElement.z + size.z/2);
                    this.#mesh.layers.set(PlanogramRendererResources.ORTHOGRAPHIC_LAYER);
                }
            }else{
                this.#mesh.layers.set(PlanogramRendererResources.GENERAL_LAYER);
                this.#mesh.scale.set(this.width, this.height, this.depth);
            }

            const container = PlanogramContainer.get(this);
            this.applyShowPositionsThroughBlocking(container?.showPositionsThroughBlocking ?? false);
        }
    }
    attributeChangedCallback(name, oldValue, newValue){
        if (this.#firstRender || oldValue === newValue) return;
        if(this.isAttached && ![PlanogramObject.Attributes.X,PlanogramObject.Attributes.Y,PlanogramObject.Attributes.Z ].includes(name))
            super.attributeChangedCallback(name, oldValue, newValue);
        this.#generateLabels();
    }

    connectedCallback(){
        this.#container = PlanogramContainer.get(this);
        super.connectedCallback();
        if(this.innerText === "" && !Array.from(this.childNodes).some(n => n.nodeType === Node.TEXT_NODE)) {
            this.appendChild(document.createTextNode(""));
        }
        this.#observer.observe(this, { characterData: true, subtree: true, childList: true });
        this.#firstRender = false;
        this.#updateMesh();
        this.#generateLabels();
    }

    disconnectedCallback(){  this.dispose(); }

    #disposefrontMesh(){
        this.#frontMesh.removeFromParent();
        this.#frontMesh.geometry.dispose();
        this.#frontMesh = null;
    }
    #disposeMesh(){
        this.#mesh.removeFromParent();
        this.#mesh.geometry.dispose();
        this.#mesh = null;
    }

    dispose(){
        this.#disposeMesh();
        this.#disposefrontMesh();
        this.#observer.disconnect();
        this.#container.textController.removeImage(this);
        super.dispose();
    }

    //#region Enums
    static textWrapping = {
        None: "none",
        Letter: "letter",
        Word: "word"
    }

    static horizontalPlacement = {
        Left: "left",
        Center: "center",
        Right: "right"
    }

    static verticalPlacement = {
        Top: "top",
        Center: "center",
        Bottom: "bottom"
    }

    static textAlignment = {
        Left: "left",
        Center: "center",
        Right: "right",
        Justify: "justify"
    }

    static textDirection = {
        Horizontal: "horizontal",
        Vertical: "vertical"
    }

    //#endregion
}

export class PlanogramTextController{
        /**@type {THREE.DataArrayTexture} */
        #texture;
        /**@type {PlanogramContainer} */
        #container;
        /**@type {Map<string, PlanogramText>} */
        #map = new Map();
        /**@type {THREE.ShaderMaterial} */
        #shader = null;
    
        #currentTextureDepth = 10;
        #depthIncrement = 10;
    
        get texture(){ return this.#texture; }
    
        /**
         * 
         * @param {THREE.ShaderMaterial} shader 
         */
        constructor( shader ){ 
            this.#shader = shader;
            this.updateData();
        }
    
        /**
         * 
         * @param {PlanogramText} planText 
         */
        removeImage( planText ){
            if(!this.#map.has(planText.uid)) return;
            const isConnected = this.#map.get(planText.uid).isConnected;
            this.#map.delete(planText.uid);
            if(isConnected)this.updateData();
        }
    
        updateData() {
            const filteredValues = [...this.#map.values()].filter(x => x.data !== null && x.isConnected);
            
            const values = filteredValues.flatMap(x => [x.data.x.imagedata, x.data.y.imagedata, x.data.z.imagedata]);
            const { width, height } = PlanogramTextController.maxRestriction;
        
            const depth = values.length;
            const data = new Uint8Array(width * height * depth * 4);
            values.forEach((item, index) => data.set(item.data, index * width * height * 4));
            this.#texture = new THREE.DataArrayTexture(data, width, height, depth);
            this.#texture.format = THREE.RGBAFormat;
            this.#texture.type = THREE.UnsignedByteType;
            this.#texture.minFilter = THREE.NearestFilter;
            this.#texture.magFilter = THREE.NearestFilter;
            this.#shader.uniforms.image.value = this.#texture;
            this.#texture.needsUpdate = true;

            filteredValues.forEach(item => item.updateAttributes());

        }
    
        updateImage( planText ){
            if(!this.#map.has(planText.uid))
                this.#map.set(planText.uid, planText);
            if(planText.data.x && planText.data.y && planText.data.z){
                this.updateData();
            }else{
                console.log("something isn't going right");
            }
        }
    
        /**
         * 
         * @param {PlanogramText} planText 
         * @returns {[number,number,number]|null}
         */
        getImageDepth(planText) {
            const values = [...this.#map.values()].filter(x=>x.data !== null && x.isConnected);
            const index = values.findIndex(x=>x.uid === planText.uid) * 3; 
            return index > -1 ? [index, index + 1, index + 2]: null;
        }
    
        static maxRestriction = {
            width: 512,
            height: 512
        } 
}